// <copyright file="CustomerPricePoint.ext.tsx" company="clarity-ventures.com">
// Copyright (c) clarity-ventures.com. All rights reserved.
// </copyright>
// Combined: price point assignment (select / create) + CSV bulk import. Assignment always renders first.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@remix-run/react";
import { AxiosError } from "axios";
import { ClientOnly } from "@core/components/client-only";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { useApi } from "@core/api";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@core/components/ui/select";
import { useToast } from "@core/components/ui/use-toast";
import { FileDropper } from "@core/components/FileDropper";
import type { CustomerPricePointPriceImportResultDto } from "@/apidtos";

const assignmentSectionKey = "ui.customerPricePointAssignment.sectionTitle";
const bulkSectionKey = "ui.customerPricePointBulk.sectionTitle";
const NONE_VALUE = "__none__";

function describeError(e: unknown, fallback: string): string {
  if (e instanceof AxiosError) {
    const body = e.response?.data as string | { detail?: string; title?: string; message?: string } | undefined;
    if (typeof body === "string" && body.trim()) {
      return body;
    }
    if (body && typeof body === "object") {
      return body.detail || body.title || body.message || e.message || fallback;
    }
    return e.message || fallback;
  }
  if (e instanceof Error) {
    return e.message || fallback;
  }
  return fallback;
}

const PricePointAssignmentBlock = ({ customerId }: { customerId: number }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { setAccordionValue, accordionValue } = useEditorFrameContext();
  const sectionTitle = t(assignmentSectionKey, "Price point assignment");

  useEffect(() => {
    if (!accordionValue.includes(sectionTitle)) {
      setAccordionValue((prev) => [...prev, sectionTitle]);
    }
  }, [accordionValue, sectionTitle, setAccordionValue]);

  const q = useQuery({
    queryKey: ["customer-price-point-assignment", customerId],
    queryFn: () => api.GetCustomerPricePointAssignment(customerId),
  });

  const [pendingId, setPendingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (q.data) {
      setPendingId(q.data.CurrentPricePointId ?? null);
    }
  }, [q.data]);

  const options = useMemo(() => q.data?.AvailablePricePoints ?? [], [q.data]);
  const currentId = q.data?.CurrentPricePointId ?? null;
  const isDirty = pendingId !== currentId;

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["customer-price-point-assignment", customerId] }),
      queryClient.invalidateQueries({ queryKey: ["customer-price-point-prices-context", customerId] }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.PutCustomerPricePointAssignment(customerId, { PricePointId: pendingId });
    },
    onSuccess: async () => {
      await invalidate();
      toast({
        title: t("ui.saved", "Saved"),
        description: t("ui.customerPricePointAssignment.saved", "Customer price point updated."),
      });
    },
    onError: (e: unknown) => {
      toast({
        title: t("ui.error", "Error"),
        description: describeError(e, t("ui.customerPricePointAssignment.saveFailed", "Could not update price point.")),
        variant: "destructive",
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.PostCustomerPricePointCreateAndAssign(customerId, {
        Name: newName.trim(),
        SortOrder: null,
      });
    },
    onSuccess: async (created) => {
      await invalidate();
      setCreating(false);
      setNewName("");
      setPendingId(created.Id);
      toast({
        title: t("ui.created", "Created"),
        description: t(
          "ui.customerPricePointAssignment.createdAndAssigned",
          "Created “{{name}}” and assigned to this customer.",
          { name: created.Name ?? `#${created.Id}` },
        ),
      });
    },
    onError: (e: unknown) => {
      toast({
        title: t("ui.error", "Error"),
        description: describeError(e, t("ui.customerPricePointAssignment.createFailed", "Could not create price point.")),
        variant: "destructive",
      });
    },
  });

  if (q.isLoading || q.isError) {
    return (
      <EditorSection title={sectionTitle}>
        <p className="text-sm text-muted-foreground">
          {q.isError
            ? t("ui.customerPricePointAssignment.loadFailed", "Could not load price point information.")
            : t("ui.loading", "Loading…")}
        </p>
      </EditorSection>
    );
  }

  return (
    <EditorSection title={sectionTitle}>
      <p className="mb-3 text-sm text-muted-foreground">
        {t(
          "ui.customerPricePointAssignment.intro",
          "Choose the price point for this customer or create a new one. Assigning a price point determines which Product Price → Price Point rows are used at checkout.",
        )}
      </p>

      {options.length === 0 ? (
        <p className="mb-3 text-sm text-amber-800 dark:text-amber-200">
          {t(
            "ui.customerPricePointAssignment.noPricePointsInCatalog",
            "There are no price points in the catalog yet. Use “Create new…” below to add one.",
          )}
        </p>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-[minmax(0,20rem)_auto_auto] sm:items-end">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t("ui.customerPricePointAssignment.pickerLabel", "Price point")}</span>
          <Select
            value={pendingId == null ? NONE_VALUE : String(pendingId)}
            onValueChange={(v) => setPendingId(v === NONE_VALUE ? null : Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("ui.customerPricePointAssignment.placeholder", "Select a price point…")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>
                {t("ui.customerPricePointAssignment.none", "(none — clear assignment)")}
              </SelectItem>
              {options.map((o) => (
                <SelectItem key={o.Id} value={String(o.Id)}>
                  {o.Name?.trim() ? o.Name : t("ui.customerPricePointAssignment.unnamedOption", "Price point #{{id}}", { id: o.Id })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <Button
          type="button"
          size="sm"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? t("ui.saving", "Saving…") : t("ui.save", "Save")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={creating}
          onClick={() => setCreating(true)}
        >
          {t("ui.customerPricePointAssignment.createNew", "Create new…")}
        </Button>
      </div>

      {creating ? (
        <div className="mt-4 rounded-md border border-border bg-muted/30 p-3">
          <h4 className="mb-2 text-sm font-medium">
            {t("ui.customerPricePointAssignment.createTitle", "Create and assign new price point")}
          </h4>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-end">
            <label className="flex flex-col gap-1 text-sm">
              <span>{t("ui.name", "Name")}</span>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("ui.customerPricePointAssignment.namePlaceholder", "e.g. Wholesale Tier 1") ?? ""}
                autoFocus
              />
            </label>
            <Button
              type="button"
              size="sm"
              disabled={newName.trim().length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending
                ? t("ui.creating", "Creating…")
                : t("ui.customerPricePointAssignment.createAndAssign", "Create and assign")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={createMutation.isPending}
              onClick={() => {
                setCreating(false);
                setNewName("");
              }}
            >
              {t("ui.cancel", "Cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </EditorSection>
  );
};

const PricePointBulkBlock = ({ customerId }: { customerId: number }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { setAccordionValue, accordionValue } = useEditorFrameContext();
  const sectionTitle = t(bulkSectionKey, "Price point prices (bulk)");

  useEffect(() => {
    if (!accordionValue.includes(sectionTitle)) {
      setAccordionValue((prev) => [...prev, sectionTitle]);
    }
  }, [accordionValue, sectionTitle, setAccordionValue]);

  const ctxQuery = useQuery({
    queryKey: ["customer-price-point-prices-context", customerId],
    queryFn: () => api.GetCustomerPricePointPriceContext(customerId),
  });

  const [file, setFile] = useState<File | null>(null);
  const [lastResult, setLastResult] = useState<CustomerPricePointPriceImportResultDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const blob = await api.GetCustomerPricePointPriceTemplate(customerId);
      if (blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        let msg = text;
        try {
          const j = JSON.parse(text) as { title?: string; detail?: string };
          msg = j.detail || j.title || text;
        } catch {
          /* keep text */
        }
        throw new Error(msg || "Could not download template.");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customer-${customerId}-price-point-prices.csv`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast({
        title: t("ui.downloaded", "Downloaded"),
        description: t(
          "ui.customerPricePointBulk.templateDownloaded",
          "CSV template with Product Key and Price.",
        ),
      });
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof AxiosError
          ? (e.response?.data as string | { detail?: string })?.toString?.() ?? e.message
          : e instanceof Error
            ? e.message
            : String(e);
      toast({
        title: t("ui.error", "Error"),
        description: msg,
        variant: "destructive",
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (f: File) => api.PostCustomerPricePointPriceImport(customerId, f),
    onSuccess: async (data) => {
      setLastResult(data);
      await queryClient.invalidateQueries({ queryKey: ["customer-price-point-prices-context", customerId] });
      toast({
        title: t("ui.importComplete", "Import complete"),
        description: t("ui.customerPricePointBulk.importSummary", "Updated {{count}} price(s) from {{rows}} row(s).", {
          count: data.PricesUpdated,
          rows: data.RowsRead,
        }),
      });
    },
    onError: (e: unknown) => {
      const ax = e instanceof AxiosError ? e : null;
      const body = ax?.response?.data;
      const msg =
        typeof body === "string"
          ? body
          : body && typeof body === "object" && "detail" in body
            ? String((body as { detail?: string }).detail)
            : ax?.message ?? String(e);
      toast({
        title: t("ui.error", "Error"),
        description: msg,
        variant: "destructive",
      });
    },
  });

  const onFilesDropped = useCallback((list: FileList) => {
    const first = list.item(0) ?? list[0];
    setFile(first ?? null);
    setLastResult(null);
  }, []);

  if (ctxQuery.isLoading || ctxQuery.isError) {
    return (
      <EditorSection title={sectionTitle}>
        <p className="text-sm text-muted-foreground">
          {ctxQuery.isError
            ? t("ui.customerPricePointBulk.loadFailed", "Could not load price point information.")
            : t("ui.loading", "Loading…")}
        </p>
      </EditorSection>
    );
  }

  const ctx = ctxQuery.data;
  if (!ctx?.HasPricePoint) {
    return (
      <EditorSection title={sectionTitle}>
        <p className="text-sm text-muted-foreground">
          {t(
            "ui.customerPricePointBulk.noPricePoint",
            "Assign a price point in the Price point assignment section above, then save. After that, CSV download and bulk import are available here.",
          )}
        </p>
      </EditorSection>
    );
  }

  const warnings = lastResult?.Warnings ?? [];

  return (
    <EditorSection title={sectionTitle}>
      <p className="mb-3 text-sm text-muted-foreground">
        {t(
          "ui.customerPricePointBulk.intro",
          "Download a CSV listing products linked to price point “{{name}}”, edit the Price column, then upload. Columns: Product Key, Price (base).",
          { name: ctx.PricePointName ?? t("ui.customerPricePointBulk.unnamed", "(unnamed)") },
        )}
      </p>
      <p className="mb-3 text-xs text-muted-foreground">
        {t("ui.customerPricePointBulk.rowCount", "{{count}} product price row(s).", { count: ctx.ProductRowCount })}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={downloadMutation.isPending}
          onClick={() => downloadMutation.mutate()}
        >
          {downloadMutation.isPending
            ? t("ui.downloading", "Downloading…")
            : t("ui.customerPricePointBulk.downloadTemplate", "Download CSV template")}
        </Button>
      </div>

      <div className="mt-6 space-y-2">
        <h4 className="text-sm font-medium">{t("ui.customerPricePointBulk.uploadLabel", "Upload filled template")}</h4>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            setLastResult(null);
            e.target.value = "";
          }}
        />
        <FileDropper
          onFilesDropped={onFilesDropped}
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className="flex min-h-[5rem] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 p-3 text-center text-sm text-muted-foreground hover:bg-muted/50"
        >
          {file ? (
            <span className="font-medium text-foreground">{file.name}</span>
          ) : (
            <span>
              {t(
                "ui.customerPricePointBulk.dropHint",
                "Drop a .csv or .xlsx file here, or click to choose…",
              )}
            </span>
          )}
        </FileDropper>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={importMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {file
              ? t("ui.customerPricePointBulk.chooseDifferent", "Choose different file…")
              : t("ui.customerPricePointBulk.chooseFile", "Choose file…")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!file || importMutation.isPending}
            onClick={() => file && importMutation.mutate(file)}
          >
            {importMutation.isPending ? t("ui.importing", "Importing…") : t("ui.import", "Import")}
          </Button>
          {file ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={importMutation.isPending}
              onClick={() => {
                setFile(null);
                setLastResult(null);
              }}
            >
              {t("ui.clear", "Clear")}
            </Button>
          ) : null}
        </div>
      </div>

      {warnings.length > 0 ? (
        <ul className="mt-4 max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-xs text-amber-800 dark:text-amber-200">
          {warnings.map((w, i) => (
            <li key={i}>
              {w.RowNumber != null
                ? t("ui.customerPricePointBulk.warnRow", "Row {{row}}: {{msg}}", { row: w.RowNumber, msg: w.Message })
                : w.Message}
            </li>
          ))}
        </ul>
      ) : null}
    </EditorSection>
  );
};

const CustomerPricePointSection = () => {
  const { id } = useParams();
  const customerId = id ? Number(id) : NaN;
  if (!Number.isFinite(customerId)) {
    return null;
  }
  return (
    <>
      <PricePointAssignmentBlock customerId={customerId} />
      <PricePointBulkBlock customerId={customerId} />
    </>
  );
};

const CustomerPricePointExtension = () => (
  <ClientOnly>
    <CustomerPricePointSection />
  </ClientOnly>
);

registerExtension(
  "admin.accounts.customers_.$id.additionalSections",
  CustomerPricePointExtension,
  "CustomerPricePointExtension",
);
